/**
 * Firebase Configuration for Darasani Web App
 * 
 * IMPORTANT: Update REACT_APP_FIREBASE_APP_ID with your web app ID from Firebase Console
 * Go to: Firebase Console > Project Settings > Your apps > Web app
 */
import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyBcp_ELVTPAjdr6YHKqEMXKyDHhR-8bKx4",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "darasanimobile.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "darasanimobile",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "darasanimobile.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "24436238800",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:24436238800:web:81ec3cd6af802b6dc631d5",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-RKC1G7W8TE"
};

// Initialize Firebase
let app;
let auth;
let googleProvider;

export const initializeFirebase = () => {
  if (!getApps().length) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    googleProvider = new GoogleAuthProvider();
    
    // Set persistence to local (survives browser restarts)
    setPersistence(auth, browserLocalPersistence).catch(console.error);
  } else {
    app = getApp();
    auth = getAuth(app);
    googleProvider = new GoogleAuthProvider();
  }
  
  return { app, auth };
};

// Get Firebase Auth instance
export const getFirebaseAuth = () => {
  if (!auth) {
    initializeFirebase();
  }
  return auth;
};

// Email/Password Sign In
export const signInWithEmail = async (email, password) => {
  try {
    const auth = getFirebaseAuth();
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const idToken = await userCredential.user.getIdToken();
    return {
      success: true,
      user: userCredential.user,
      idToken
    };
  } catch (error) {
    console.error('Email sign in error:', error);
    return {
      success: false,
      error: getFirebaseErrorMessage(error.code)
    };
  }
};

// Email/Password Sign Up
export const signUpWithEmail = async (email, password) => {
  try {
    const auth = getFirebaseAuth();
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const idToken = await userCredential.user.getIdToken();
    return {
      success: true,
      user: userCredential.user,
      idToken
    };
  } catch (error) {
    console.error('Email sign up error:', error);
    return {
      success: false,
      error: getFirebaseErrorMessage(error.code)
    };
  }
};

// Google Sign In (Web popup)
export const signInWithGoogle = async () => {
  try {
    const auth = getFirebaseAuth();
    const result = await signInWithPopup(auth, googleProvider);
    const idToken = await result.user.getIdToken();
    
    return {
      success: true,
      user: result.user,
      idToken
    };
  } catch (error) {
    console.error('Google sign in error:', error);
    return {
      success: false,
      error: error.message || 'Google sign in failed'
    };
  }
};

// Sign Out
export const firebaseSignOut = async () => {
  try {
    const auth = getFirebaseAuth();
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Sign out error:', error);
    return { success: false, error: error.message };
  }
};

// Listen to auth state changes
export const onFirebaseAuthStateChanged = (callback) => {
  const auth = getFirebaseAuth();
  return onAuthStateChanged(auth, callback);
};

// Get current user's ID token
export const getCurrentUserToken = async () => {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (user) {
    return await user.getIdToken(true);
  }
  return null;
};

// Helper to get user-friendly error messages
const getFirebaseErrorMessage = (errorCode) => {
  const errorMessages = {
    'auth/email-already-in-use': 'This email is already registered',
    'auth/invalid-email': 'Invalid email address',
    'auth/operation-not-allowed': 'Email/password sign in is not enabled',
    'auth/weak-password': 'Password is too weak (min 6 characters)',
    'auth/user-disabled': 'This account has been disabled',
    'auth/user-not-found': 'No account found with this email',
    'auth/wrong-password': 'Incorrect password',
    'auth/invalid-credential': 'Invalid email or password',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later',
    'auth/network-request-failed': 'Network error. Please check your connection',
    'auth/popup-closed-by-user': 'Sign in was cancelled',
  };
  return errorMessages[errorCode] || 'Authentication failed. Please try again.';
};

export default {
  initializeFirebase,
  getFirebaseAuth,
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  firebaseSignOut,
  onFirebaseAuthStateChanged,
  getCurrentUserToken
};
